import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { getTextField, ValidationError } from '@lender/utils';
import { Button, Typography } from '@mui/material';
import ModalFormWrapper from '@sharedCR/coreDesignSystem/ModalFormWrapper';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import {
  FormAutocompleteOption,
  onAutocompleteChangeInForm,
} from 'app/shared/components/form/Autocomplete';
import TextFieldBuilder from 'app/shared/components/form/TextFieldBuilder';
import { AutocompleteCDS, TextFieldCDS } from 'app/shared/componentsRestyled/coreDesignSystem/form';
import CreateFormModal from 'app/shared/modules/CreateFormModal';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { object } from 'yup';

import { FeatureFlag } from 'constants/featureFlags';
import { LoxDocumentTypeName } from 'constants/forms';
import { entityAssignmentTypes } from 'constants/options';
import { NATIONAL_CATEGORY_CODE } from 'constants/states';
import { DataType, EntityAssignmentType } from 'constants/taskCategories';
import { TaskAssignmentType, taskAssignmentTypeOptions } from 'constants/tasks';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useForm from 'hooks/useForm';
import { getConditionalArray } from 'utils/data';
import { getPagePermissions } from 'utils/permissions';
import { replaceValuesInsideCurlyBrackets } from 'utils/string';
import {
  getBorrowerPairError,
  getCompatibleBorrowerOptions,
  getIncomeOwnerOption,
  getOptionsWithSelected,
  getSoleLegalAssignee,
  getTaskEntityOptions,
} from 'utils/taskEntity';
import { getTextFieldBuilder } from 'utils/validation';

import { getMasterPermissions } from 'store/auth/selectors';
import { getFormsInfoAction, openFormPreviewAction } from 'store/forms/actions';
import {
  selectFormsInfoListOptions,
  selectIsFormsInfoLoaded,
  selectLoanSpecificFormsListOptions,
  selectStateOptionsWithExistingFormsInfo,
} from 'store/forms/selectors';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  addTaskAction,
  getTaskSlaGroupsByApplicationIdAction,
  resetTaskFieldsDraftAction,
  saveTaskFieldsAsDraftAction,
} from 'store/loanApplications/actions';
import {
  selectAssetsWithTypeAndAmount,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectIncomesWithTypeAndAmount,
  selectLoanApplicationBorrowerOptions,
} from 'store/loanApplications/selectors';
import { selectGroupedTaskTypeOptions, selectTaskTypeDynamicField } from 'store/system/selectors';
import { selectSlaGroupsOptions } from 'store/underwriting/selectors';

import { FormVariant, formVariantsOptions } from './Tasks.constants';
import { TasksTestIds } from './Tasks.testIds';
import { getSubmitErrorText } from './Tasks.utils';
import TaskTypeOption from './TaskTypeOption';

const optionField = object().nullable().required(ValidationError.RequiredField);

const getSchema = ({
  taskTypeDynamicFieldOptions,
  isEntity,
  isNewTaskAssignmentEngine,
  isSlaGroupsAssigneeSelected,
}) => {
  return object().shape({
    taskTypeOption: optionField,
    ...(isNewTaskAssignmentEngine && {
      entity: isEntity ? optionField : object().nullable(),
      ...(!isSlaGroupsAssigneeSelected && {
        borrower: optionField,
      }),
    }),
    description: getTextFieldBuilder(taskTypeDynamicFieldOptions, {
      singleOption: false,
    }).nullable(),
    formVariant: getTextField(),
    state: getTextField({ isRequired: false }),
    formId: getTextField(),
  });
};

const TaskAssignmentForm = ({
  isLoading,
  setIsLoading,
  formRef,
  isHideStateField,
  selectedForm,
  onFinishUploadForm,
}) => {
  const { applicationId } = useParams();

  const [submitError, setSubmitError] = useState(null);

  const dispatch = useDispatch();
  const isOldTaskAssignmentEngine = useFeatureToggle(FeatureFlag.OldTaskAssignmentEngine);
  const isNewTaskAssignmentEngine = !isOldTaskAssignmentEngine;

  const masterPermissions = useSelector(getMasterPermissions);
  const taskTypeOptions = useSelector(selectGroupedTaskTypeOptions);
  const taskTypeDynamicFieldOptions = useSelector(selectTaskTypeDynamicField);
  const borrowers = useSelector(selectLoanApplicationBorrowerOptions);
  const assets = useSelector(selectAssetsWithTypeAndAmount);
  const incomes = useSelector(selectIncomesWithTypeAndAmount);
  const slaGroupOptions = useSelector(selectSlaGroupsOptions);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.ASSIGN_TASK));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.ASSIGN_TASK));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.ASSIGN_TASK));

  const formsOptions = useSelector(selectFormsInfoListOptions);
  const loanSpecificFormsOptions = useSelector(selectLoanSpecificFormsListOptions);
  const statesOptionsWithExistingForms = useSelector(selectStateOptionsWithExistingFormsInfo);
  const isFormsInfoLoaded = useSelector(selectIsFormsInfoLoaded);

  const isDisabledByLock = isLoading || isPageLocked;

  const isBorrowerSingleOption = borrowers.length === 1 && isNewTaskAssignmentEngine;

  const {
    form,
    isFormChanged,
    setSchema,
    validateForm,
    setInForm,
    validationErrors,
    validateField,
    handleChange,
    setValidationErrors,
  } = useForm({
    taskTypeOption: '',
    description: '',
    formVariant: { name: FormVariant.ALL_FORMS, value: FormVariant.ALL_FORMS },
    state: { name: '', value: '' },
    formId: {
      name: selectedForm?.name || '',
      value: selectedForm?.formId || '',
      formType: selectedForm?.formType || '',
    },
    borrower: isBorrowerSingleOption ? borrowers[0] : '',
    taskBorrower: isBorrowerSingleOption ? borrowers[0] : '',
    entity: null,
  });

  const isAllFormsSelected = form.formVariant.value === FormVariant.ALL_FORMS;
  const isFormsDataTypeSelected = form.taskTypeOption?.dataType === DataType.FORMS;
  const isCreateCustomFormSelected =
    form.formVariant.value === FormVariant.CREATE_NEW_FORM && isFormsDataTypeSelected;
  const isFormSelectionIncomplete =
    isFormsDataTypeSelected && !form.formId?.value && !isCreateCustomFormSelected;
  const isSlaGroupsAssigneeSelected =
    form.taskTypeOption?.taskAssignmentType === TaskAssignmentType.SLA_GROUP;
  const [selectedSlaGroup, selectedAssignmentType] = useMemo(
    () =>
      getConditionalArray(form.taskTypeOption, () => [
        slaGroupOptions.find(({ value }) => value === form.taskTypeOption.slaGroupId),
        taskAssignmentTypeOptions.find(
          ({ value }) => value === form.taskTypeOption.taskAssignmentType,
        ),
      ]),
    [form.taskTypeOption, slaGroupOptions],
  );

  const filteredFormVariantOptions = useMemo(() => {
    return formVariantsOptions.filter(
      ({ permissions }) =>
        !permissions ||
        permissions.some(
          permission => getPagePermissions(permission)(masterPermissions).isManageAllowed,
        ),
    );
  }, [masterPermissions]);

  const filteredFormOptions = useMemo(() => {
    const filteredFormsOptions = formsOptions.filter(({ state }) => state === form.state.value);

    if (filteredFormsOptions.length) {
      return filteredFormsOptions;
    }

    return formsOptions;
  }, [form.state, formsOptions]);

  const { entityAssignmentType } = form.taskTypeOption || {};

  const isBorrowerAssignmentTypeSelected = entityAssignmentType === EntityAssignmentType.BORROWER;
  const isIncomeAssignmentTypeSelected = entityAssignmentType === EntityAssignmentType.INCOME;
  const shouldDeriveAssigneeFromIncome =
    isIncomeAssignmentTypeSelected && !isSlaGroupsAssigneeSelected;

  const entityLabel = useMemo(
    () => entityAssignmentTypes.find(({ value }) => value === entityAssignmentType)?.name,
    [entityAssignmentType],
  );

  // The entity list is never filtered by the assignee — filtering both ways latched the pair
  // (FH-1986). An incompatible combination is reachable by design and is reported by
  // borrowerPairError below.
  const selectedEntityOptions = useMemo(
    () => getTaskEntityOptions({ entityAssignmentType, borrowers, incomes, assets }),
    [entityAssignmentType, borrowers, incomes, assets],
  );

  const compatibleAssigneeOptions = useMemo(
    () =>
      isBorrowerAssignmentTypeSelected
        ? getOptionsWithSelected(
            getCompatibleBorrowerOptions(borrowers, form.entity),
            form.borrower,
          )
        : borrowers,
    [isBorrowerAssignmentTypeSelected, borrowers, form.entity, form.borrower],
  );

  const soleAssigneeOption = useMemo(
    () =>
      isBorrowerAssignmentTypeSelected
        ? getSoleLegalAssignee(borrowers, form.entity, form.borrower)
        : null,
    [isBorrowerAssignmentTypeSelected, borrowers, form.entity, form.borrower],
  );

  const borrowerPairError = useMemo(
    () =>
      isBorrowerAssignmentTypeSelected ? getBorrowerPairError(form.entity, form.borrower) : null,
    [isBorrowerAssignmentTypeSelected, form.entity, form.borrower],
  );

  const isSubmitButtonDisabled = isFormSelectionIncomplete || !!borrowerPairError;

  // Locked only when the assignee is genuinely the single legal choice, so an invalid pair always
  // stays fixable from either side.
  const isAssigneeLocked = !!soleAssigneeOption;

  const incomeOwnerOption = useMemo(
    () => (shouldDeriveAssigneeFromIncome ? getIncomeOwnerOption(form.entity, borrowers) : null),
    [shouldDeriveAssigneeFromIncome, form.entity, borrowers],
  );

  const isEntity = !!selectedEntityOptions;
  useEffect(() => {
    setSchema(
      getSchema({
        taskTypeDynamicFieldOptions,
        isEntity,
        isNewTaskAssignmentEngine,
        isSlaGroupsAssigneeSelected,
      }),
    );
  }, [
    isNewTaskAssignmentEngine,
    selectedEntityOptions,
    setSchema,
    taskTypeDynamicFieldOptions,
    isSlaGroupsAssigneeSelected,
    isEntity,
  ]);

  useEffect(() => {
    if (!shouldDeriveAssigneeFromIncome) {
      return;
    }

    setInForm('borrower', incomeOwnerOption);
  }, [shouldDeriveAssigneeFromIncome, incomeOwnerOption, setInForm]);

  // Only fills an EMPTY assignee — overwriting a deliberate pick would be the silent discard this
  // validation exists to remove.
  useEffect(() => {
    if (!soleAssigneeOption || form.borrower?.value) {
      return;
    }

    setInForm('borrower', soleAssigneeOption);
  }, [soleAssigneeOption, form.borrower, setInForm]);

  useEffect(() => {
    setInForm('description', form.taskTypeOption?.description || '', false);
  }, [setInForm, form.taskTypeOption?.description]);

  useEffectSafeStrictMode(() => {
    if (!isFormsDataTypeSelected || isFormsInfoLoaded) {
      return;
    }

    setIsLoading(true);
    dispatch(getFormsInfoAction())
      .catch(Error)
      .finally(() => {
        setIsLoading(false);
      });
  }, [dispatch, isFormsDataTypeSelected, isFormsInfoLoaded, setIsLoading]);

  useEffect(() => {
    if (selectedForm) {
      return;
    }

    setInForm('formId', { name: '', value: '', formType: '' });
  }, [form.formVariant.value, setInForm, selectedForm]);

  const onPreviewFormClick = useCallback(() => {
    dispatch(
      openFormPreviewAction({
        formId: form.formId?.value,
        formType: form.formId?.formType,
        selectedPage: 1,
      }),
    );
  }, [dispatch, form.formId?.formType, form.formId?.value]);

  const getFormattedTaskData = useCallback(
    () => ({
      taskTypeId: form.taskTypeOption.taskTypeId,
      metadata: form.taskTypeOption.metadataTemplate ?? null,
      applicationId,
      name: replaceValuesInsideCurlyBrackets({
        string: form.taskTypeOption.name,
        options: taskTypeDynamicFieldOptions,
        keyToReplace: 'name',
        keyToReplaceWith: 'value',
      }),
      ...(isNewTaskAssignmentEngine && {
        taskEntityId: isBorrowerAssignmentTypeSelected
          ? form?.entity.value
          : form?.entity?.incomeId || form?.entity?.assetId,
        ...(!isSlaGroupsAssigneeSelected && { borrowerId: form.borrower?.value }),
      }),
      description: replaceValuesInsideCurlyBrackets({
        string: form.description,
        options: taskTypeDynamicFieldOptions,
        keyToReplace: 'name',
        keyToReplaceWith: 'value',
      }),
      formName: form.formId?.name,
    }),
    [
      form.taskTypeOption,
      form?.entity,
      form.description,
      form.borrower,
      form.formId,
      applicationId,
      taskTypeDynamicFieldOptions,
      isNewTaskAssignmentEngine,
      isSlaGroupsAssigneeSelected,
      isBorrowerAssignmentTypeSelected,
    ],
  );

  const onSubmit = useCallback(
    async e => {
      e.preventDefault();

      if (!(await validateForm())) {
        return;
      }

      setSubmitError(null);
      setIsLoading(true);
      dispatch(
        addTaskAction({
          formBundleId: form.formId?.value,
          availableForAssignees: true,
          ...getFormattedTaskData(),
        }),
      )
        .then(() => {
          dispatch(
            showMessageAction({
              message: 'Task assigned',
              variant: 'success',
            }),
          );
          dispatch(closeDialogAction());
          dispatch(getTaskSlaGroupsByApplicationIdAction({ applicationId }));
        })
        .catch(error => setSubmitError(getSubmitErrorText(error)))
        .finally(() => setIsLoading(false));
    },
    [validateForm, setIsLoading, dispatch, form.formId?.value, getFormattedTaskData, applicationId],
  );

  const onSubmitCreateNewForm = useCallback(
    event => {
      event.preventDefault();

      const abortController = new AbortController();

      const formattedTaskData = getFormattedTaskData();
      dispatch(saveTaskFieldsAsDraftAction(formattedTaskData));

      const onFinishFormUploading = uploadedForm => {
        const formParams = {
          name: uploadedForm.name,
          formId: uploadedForm.formId,
          historyEntityId: undefined,
          formType: uploadedForm.formType,
          selectedPage: 1,
        };

        onFinishUploadForm(formParams);
      };

      dispatch(
        openDialogAction({
          header: 'Create new loan specific form',
          body: dialogProps => (
            <CreateFormModal
              isDisableResetStateAction
              isLoanSpecificForm
              isPdfOnly
              abortController={abortController}
              applicationId={applicationId}
              defaultDocumentTypeName={LoxDocumentTypeName}
              formAreaCode={NATIONAL_CATEGORY_CODE}
              onFinishFormUploading={onFinishFormUploading}
              {...dialogProps}
            />
          ),
          afterCloseCallback: () => {
            dispatch(resetTaskFieldsDraftAction());
            abortController.abort();
          },
        }),
      );
    },
    [applicationId, dispatch, getFormattedTaskData, onFinishUploadForm],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setAutocompleteValueInForm = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm],
  );

  const onAutocompleteChange = useCallback(
    (e, value) => {
      setSubmitError(null);
      setAutocompleteValueInForm(e, value);
    },
    [setAutocompleteValueInForm],
  );

  const onFieldChange = useCallback(
    e => {
      setSubmitError(null);
      handleChange(e);
    },
    [handleChange],
  );

  const onControlAutocompleteChange = useCallback(
    (e, value) => {
      const currentFieldName = e.currentTarget.getAttribute('data-fieldname');
      setValidationErrors({});

      switch (currentFieldName) {
        case 'taskTypeOption': {
          setInForm('entity', null);

          if (!isBorrowerSingleOption) {
            setInForm('borrower', null);
          }

          if (
            isBorrowerSingleOption &&
            value.entityAssignmentType === EntityAssignmentType.BORROWER
          ) {
            setInForm('entity', borrowers[0]);
          }

          break;
        }

        default:
          break;
      }

      onAutocompleteChange(e, value);
    },
    [setValidationErrors, onAutocompleteChange, setInForm, isBorrowerSingleOption, borrowers],
  );

  const onGroupOptions = useCallback(({ group }) => group, []);

  const onGroupTaskTypesByScope = useCallback(({ groupName }) => groupName, []);

  return (
    <ModalFormWrapper
      isWithCustomButtons
      formRef={formRef}
      formTestId="assign-task-form"
      isFormChanged={isFormChanged}
      isSubmitting={isLoading}
      onSubmit={isCreateCustomFormSelected ? onSubmitCreateNewForm : onSubmit}
    >
      <AutocompleteCDS
        disableClearable
        required
        OptionContent={TaskTypeOption}
        containerClassName="mb-24"
        dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_TASK_TYPE_INPUT}
        dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_TASK_TYPE_OPTION}
        disabled={isDisabledByLock || lockedFieldsMap.taskTypeOption}
        error={!!validationErrors.taskTypeOption}
        groupBy={onGroupTaskTypesByScope}
        helperText={validationErrors.taskTypeOption}
        isHidden={hiddenFieldsMap.taskTypeOption}
        label="Task Type"
        name="taskTypeOption"
        optionKey="taskTypeId"
        options={taskTypeOptions}
        value={form.taskTypeOption}
        onChange={onControlAutocompleteChange}
      />

      {selectedEntityOptions && isNewTaskAssignmentEngine && (
        <AutocompleteCDS
          required
          containerClassName="mb-24"
          dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_ENTITY_INPUT}
          dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_ENTITY_OPTION}
          disabled={isDisabledByLock || lockedFieldsMap.entity}
          error={!!validationErrors.entity || !!borrowerPairError}
          helperText={validationErrors.entity || borrowerPairError}
          isHidden={hiddenFieldsMap.entity}
          label={entityLabel}
          name="entity"
          optionKey="name"
          optionName={isBorrowerAssignmentTypeSelected ? 'name' : 'typeAndAmount'}
          options={selectedEntityOptions}
          value={form.entity}
          onChange={onControlAutocompleteChange}
        />
      )}

      {isFormsDataTypeSelected && (
        <AutocompleteCDS
          disableClearable
          required
          containerClassName="mb-24"
          dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_FORM_TYPE_INPUT}
          dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_FORM_TYPE_OPTION}
          disabled={isDisabledByLock || lockedFieldsMap.formVariant}
          error={!!validationErrors.formVariant}
          helperText={validationErrors.formVariant}
          isHidden={hiddenFieldsMap.formVariant}
          label="Form Type"
          name="formVariant"
          options={filteredFormVariantOptions}
          value={form.formVariant}
          onChange={onControlAutocompleteChange}
        />
      )}

      {isFormsDataTypeSelected && !isCreateCustomFormSelected && (
        <>
          {!isHideStateField && isAllFormsSelected && (
            <AutocompleteCDS
              containerClassName="mb-24"
              dataTestId={TasksTestIds.TASK_ASSIGNMENT_FORM_STATE_SELECT}
              disabled={isDisabledByLock || lockedFieldsMap.state}
              error={!!validationErrors.state}
              helperText={validationErrors.state}
              isHidden={hiddenFieldsMap.state}
              label="State"
              name="state"
              options={statesOptionsWithExistingForms}
              value={form.state}
              onChange={onAutocompleteChange}
            />
          )}

          <AutocompleteCDS
            required
            OptionContent={FormAutocompleteOption}
            containerClassName="mb-24"
            dataTestId={TasksTestIds.TASK_ASSIGNMENT_FORM_FORMID_SELECT}
            disabled={isDisabledByLock || !!selectedForm || lockedFieldsMap.formId}
            error={!!validationErrors.formId}
            helperText={validationErrors.formId}
            isHidden={hiddenFieldsMap.formId}
            label="Form"
            name="formId"
            options={isAllFormsSelected ? filteredFormOptions : loanSpecificFormsOptions}
            value={form.formId}
            onChange={onAutocompleteChange}
          />
        </>
      )}

      {form.taskTypeOption && (
        <>
          {isNewTaskAssignmentEngine && (
            <AutocompleteCDS
              disableClearable
              disabled
              containerClassName="mb-24"
              dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_TASKASSIGNMENTTYPE_INPUT}
              dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_TASKASSIGNMENTTYPE_OPTION}
              label="Assigned to"
              name="taskAssignmentType"
              options={taskAssignmentTypeOptions}
              value={selectedAssignmentType}
            />
          )}

          {isNewTaskAssignmentEngine && form.taskTypeOption && !isSlaGroupsAssigneeSelected && (
            <AutocompleteCDS
              required
              containerClassName="mb-24"
              dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_BORROWER_INPUT}
              dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_BORROWER_OPTION}
              // For income tasks the server derives the borrower from the income's owner
              // (Skynet.Loans FH-1730), so the assignee is auto-set and locked, not picked.
              disabled={
                isDisabledByLock ||
                lockedFieldsMap.borrower ||
                shouldDeriveAssigneeFromIncome ||
                isAssigneeLocked
              }
              error={!!validationErrors.borrower}
              helperText={validationErrors.borrower}
              isHidden={hiddenFieldsMap.borrower}
              label="Assignee"
              name="borrower"
              options={compatibleAssigneeOptions}
              value={form.borrower}
              onChange={onControlAutocompleteChange}
            />
          )}

          {isNewTaskAssignmentEngine && form.taskTypeOption && isSlaGroupsAssigneeSelected && (
            <AutocompleteCDS
              disabled
              containerClassName="mb-24"
              dataTestIdInput={TasksTestIds.TASK_ASSIGNMENT_FORM_SLAGROUP_INPUT}
              dataTestIdOption={TasksTestIds.TASK_ASSIGNMENT_FORM_SLAGROUP_OPTION}
              label="SLA Group"
              name="slaGroup"
              options={slaGroupOptions}
              value={selectedSlaGroup}
              onChange={onControlAutocompleteChange}
            />
          )}

          {isNewTaskAssignmentEngine ? (
            <TextFieldBuilder
              fullWidth
              required
              containerClassName="mb-24"
              data-testid={TasksTestIds.TASK_ASSIGNMENT_FORM_DESCRIPTION_INPUT}
              disabled={isDisabledByLock || lockedFieldsMap.description}
              error={!!validationErrors.description}
              groupBy={onGroupOptions}
              handleChange={onFieldChange}
              helperText={validationErrors.description}
              id="description"
              isHidden={hiddenFieldsMap.description}
              label="Description"
              maxLength={4000}
              name="description"
              options={taskTypeDynamicFieldOptions}
              rows={3}
              setInForm={setInForm}
              validateField={validateField}
              value={form.description || ''}
            />
          ) : (
            <TextFieldCDS
              fullWidth
              multiline
              required
              containerClassName="mb-24"
              data-testid={TasksTestIds.TASK_ASSIGNMENT_FORM_DESCRIPTION_INPUT}
              disabled={isDisabledByLock || lockedFieldsMap.description}
              error={!!validationErrors.description}
              helperText={validationErrors.description}
              id="description"
              isHidden={hiddenFieldsMap.description}
              label="Description"
              maxLength={4000}
              name="description"
              rows={3}
              type="text"
              value={form.description || ''}
              variant="outlined"
              onChange={onFieldChange}
            />
          )}
        </>
      )}

      {!!submitError && (
        <Typography
          className="mb-16"
          color="error.main"
          data-testid={TasksTestIds.TASK_ASSIGNMENT_FORM_SUBMIT_ERROR}
          variant="regular14"
        >
          {submitError}
        </Typography>
      )}

      <div className="flex justify-between">
        {isFormsDataTypeSelected && !!form.formId?.value && (
          <Button
            data-testid={TasksTestIds.TASK_ASSIGNMENT_FORM_PREVIEW_BUTTON}
            disabled={!isFormChanged || isLoading}
            type="button"
            variant="contained"
            onClick={onPreviewFormClick}
          >
            Preview
          </Button>
        )}

        <Button
          className="self-end ml-auto"
          data-testid={TasksTestIds.TASK_ASSIGNMENT_FORM_SUBMIT_BUTTON}
          disabled={!isFormChanged || isLoading || isSubmitButtonDisabled}
          type="submit"
          variant="contained"
        >
          {isCreateCustomFormSelected ? 'Create new form' : 'Assign'}
        </Button>
      </div>
    </ModalFormWrapper>
  );
};

TaskAssignmentForm.propTypes = {
  ...WithLoadingPropTypes,
};

export default withLoading(TaskAssignmentForm);
